import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useCurrency, supportedCurrencies, type Currency } from '../contexts/CurrencyContext'

const CheckoutCurrencySelector = () => {
  const { selectedCurrency, setSelectedCurrency } = useCurrency()
  const [isOpen, setIsOpen] = useState(false)

  const handleCurrencyChange = (currency: Currency) => {
    setSelectedCurrency(currency)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white hover:bg-white/10 transition-all duration-200"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center space-x-3">
          <span className="w-5 h-5 flex items-center justify-center text-blue-400 font-semibold">
            {selectedCurrency.symbol}
          </span>
          <div className="text-left">
            <p className="text-white font-medium">{selectedCurrency.symbol} {selectedCurrency.name}</p>
            <p className="text-gray-400 text-sm">{selectedCurrency.code}</p>
          </div>
        </div>
        <ChevronDownIcon 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 mt-2 w-full bg-black/90 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 z-50 overflow-hidden"
            >
              <div className="py-2">
                {supportedCurrencies.map((currency) => (
                  <motion.button
                    key={currency.code}
                    onClick={() => handleCurrencyChange(currency)}
                    className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors duration-200 flex items-center justify-between ${
                      selectedCurrency.code === currency.code
                        ? 'bg-white/10 text-white' 
                        : 'text-white/80'
                    }`}
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg font-medium w-8">{currency.symbol}</span>
                      <div>
                        <p className="font-medium">{currency.name}</p>
                        <p className="text-sm text-gray-400">{currency.code}</p>
                      </div>
                    </div>
                    {selectedCurrency.code === currency.code && (
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CheckoutCurrencySelector 